import { type ReactNode, lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/config/queryClient';
import { msalInstance } from '@/config/msalConfig';
import { ErrorBoundary } from './ErrorBoundary';
import { IdleTimeoutHandler } from './IdleTimeoutHandler';

// Loaded only in dev — excluded from production bundle by Vite dead-code elimination
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : null;

function DevTools() {
  if (!ReactQueryDevtools) return null;
  return (
    <Suspense fallback={null}>
      <ReactQueryDevtools initialIsOpen={false} />
    </Suspense>
  );
}

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: Readonly<AppProvidersProps>) {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <IdleTimeoutHandler />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            {children}
            <Toaster
              position="top-center"
              containerStyle={{ top: '56px' }}
              toastOptions={{
                duration: 5000,
                className: '[&>div:first-child]:mr-2 [&>div:last-child]:m-0 [&>div:last-child]:flex-none',
                style: {
                  width: 'calc(100vw - 32px)',
                  maxWidth: '360px',
                  padding: '12px 16px',
                },
              }}
            />
          </BrowserRouter>
          {import.meta.env.DEV && <DevTools />}
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
